import React, { useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ProductFilters = ({ 
  onProductCodeSearch,
  onCategorySearch,
  onBrandSearch,
  onClearFilters,
  onExportExcel,
  onCreateProduct,
  isGridView = false,
  onToggleView = () => {}
}) => {
  const [productCode, setProductCode] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [categories, setCategories] = useState([]);       
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const hasActiveFilters = productCode || selectedCategory || selectedBrand;

  // Fetch categories + brands
useEffect(() => {
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('_id');

      // Pick base URL depending on userId
      const baseUrl =
        userId === "56"
          ? import.meta.env.VITE_CATDEMO_BASE_URL
          : import.meta.env.VITE_CATPROD_BASE_URL;

      const response = await fetch(`${baseUrl}/category/active`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      const mapped = Array.isArray(result)
        ? result.map(cat => ({
            id: String(cat.categoryId),
            name: cat.name,
            parentId: String(cat.parentInt),
            level: cat.level,
            levels: cat.levels, // <-- Add this line
            description: cat.description || ''
          }))
        : [];
      setCategories(mapped);
    } catch (error) {
      console.error("Category fetch failed:", error);
    }
  };

  const fetchBrands = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('_id');

      // Pick base URL depending on userId
      const baseUrl =
        userId === "56"
          ? import.meta.env.VITE_CATDEMO_BASE_URL
          : import.meta.env.VITE_CATPROD_BASE_URL;

      const response = await fetch(`${baseUrl}/brand/active`, {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      setBrands(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error("Brand fetch failed:", error);
    }
  };

  fetchCategories();
  fetchBrands();
}, []);


  // --- Build Category Tree ---
  const buildCategoryTree = (flatCategories) => {
    const idToNode = {};
    flatCategories.forEach(cat => {
      idToNode[cat.id] = { ...cat, children: [] };
    });
    const tree = [];
    flatCategories.forEach(cat => {
      if (cat.parentId !== "0" && idToNode[cat.parentId]) {
        idToNode[cat.parentId].children.push(idToNode[cat.id]);
      } else {
        tree.push(idToNode[cat.id]);
      }
    });
    return tree;
  };

  const getDropdownOptions = (tree, level = 1) => {
    let options = [];
    tree.forEach(node => {
      options.push({
        value: node.id,
        label: `${'— '.repeat(level - 1)}${node.name} (Level ${node.level})`,
        disabled: node.level !== node.levels
      });
      if (node.children && node.children.length > 0) {
        options = options.concat(getDropdownOptions(node.children, level + 1));
      }
    });
    return options;
  };

  const categoryTree = React.useMemo(() => buildCategoryTree(categories), [categories]);
  const categoryOptions = React.useMemo(() => getDropdownOptions(categoryTree), [categoryTree]);
  const reactSelectOptions = React.useMemo(() => categoryOptions.map(opt => ({
    value: opt.value,
    label: opt.label,
    isDisabled: opt.disabled
  })), [categoryOptions]);

  // ✅ Trigger API when category changes
useEffect(() => {
  let didCancel = false;

  const fetchByCategory = async () => {
    if (!selectedCategory) {
      if (onCategorySearch) onCategorySearch({ status: 'clear' });
      return;
    }

    const selected = categoryOptions.find(opt => opt.value === selectedCategory);
    if (!selected || selected.isDisabled) return;

    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('_id');

      // Pick base URL depending on userId
      const baseUrl =
        userId === "56"
          ? import.meta.env.VITE_CATDEMO_BASE_URL
          : import.meta.env.VITE_CATPROD_BASE_URL;

      const response = await fetch(
        `${baseUrl}/product_details/by_category_id/${selectedCategory}/page?page=0&size=1000`,
        {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.status === 500) {
        if (onCategorySearch) {
          onCategorySearch({
            status: 'error',
            message: 'Server error. Please try another category.'
          });
        }
        return;
      }

      const result = await response.json();
      if (!didCancel && onCategorySearch) onCategorySearch(result);

    } catch (error) {
      if (!didCancel && onCategorySearch) {
        onCategorySearch({ status: 'error', message: error?.message || 'API error' });
      }
    }
  };

  fetchByCategory();
  return () => { didCancel = true; };
}, [selectedCategory]);

  // ✅ Trigger API when brand changes
useEffect(() => {
  let didCancel = false;

  const fetchByBrand = async () => {
    if (!selectedBrand) {
      if (onBrandSearch) onBrandSearch({ status: 'clear' });
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('_id');

      // Pick base URL depending on userId
      const baseUrl =
        userId === "56"
          ? import.meta.env.VITE_CATDEMO_BASE_URL
          : import.meta.env.VITE_CATPROD_BASE_URL;

      const response = await fetch(
        `${baseUrl}/product_details/by_brand_id/${selectedBrand}/page?page=0&size=1000`,
        {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const result = await response.json();
      if (!didCancel && onBrandSearch) onBrandSearch(result);

    } catch (error) {
      if (!didCancel && onBrandSearch) {
        onBrandSearch({ status: 'error', message: error?.message || 'API error' });
      }
    }
  };

  fetchByBrand();
  return () => { didCancel = true; };
}, [selectedBrand]);

const handleProductCodeSearch = async () => {
  if (!productCode.trim()) return;
  setSearchLoading(true);

  try {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('_id');

    // Pick base URL depending on userId
    const baseUrl =
      userId === "56"
        ? import.meta.env.VITE_CATDEMO_BASE_URL
        : import.meta.env.VITE_CATPROD_BASE_URL;

    const response = await fetch(
      `${baseUrl}/product_details/by_code/${productCode.trim()}`,
      {
        method: 'GET',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      }
    );

    const result = await response.json();
    if (onProductCodeSearch) {
      onProductCodeSearch(result);
    }

  } catch (error) {
    if (onProductCodeSearch) {
      onProductCodeSearch({ status: 'error', message: error?.message || 'API error' });
    }
  }

  setSearchLoading(false);
};

  useEffect(() => {
    if (productCode === '' && onProductCodeSearch) {
      onProductCodeSearch({ status: 'clear' });
    }
  }, [productCode]);   // ✅ no onProductCodeSearch dependency

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Product Management</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure and monitor product endpoints for real-time event notifications
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={onExportExcel}
          >
            Export Excel
          </Button>
        </div>
      </div>

      {/* View Toggle + Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          onClick={() => onToggleView(!isGridView)}
          className="flex items-center justify-center px-3 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
          title={isGridView ? "Switch to List View" : "Switch to Grid View"}
        >
          {isGridView ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          )}
        </button>
        <Input
          type="search"
          placeholder="Search by product code..."
          value={productCode}
          onChange={e => setProductCode(e?.target?.value)}
          className="w-56"
        />
        <Button
          variant="primary"
          style={{ backgroundColor: '#2563eb', color: 'white' }}
          loading={searchLoading}
          onClick={handleProductCodeSearch}
          className="ml-2"
        >
          Search
        </Button>

        <div className="ml-4" />

       {/* Filters Row */}
<div className="flex items-center gap-8">
  {/* Category Dropdown */}
  <div className="w-56">
    <ReactSelect
      classNamePrefix="react-select"
      options={reactSelectOptions}
      value={reactSelectOptions.find(opt => opt.value === selectedCategory) || null}
      onChange={opt => {
        setSelectedCategory(opt ? opt.value : null);
        setSelectedBrand(null); // clear brand filter when category changes
      }}
      isOptionDisabled={opt => opt.isDisabled}
      placeholder="All Categories"
      isClearable
    />
  </div>

  {/* Brand Dropdown */}
  <div className="w-56">
    <ReactSelect
      classNamePrefix="react-select"
      options={brands.map(b => ({ value: b.brand_id, label: b.brand_name }))}
      value={
        brands.length > 0 && selectedBrand
          ? { value: selectedBrand, label: brands.find(b => b.brand_id === selectedBrand)?.brand_name }
          : null
      }
      onChange={opt => {
        setSelectedBrand(opt ? opt.value : null);
        setSelectedCategory(null); // clear category filter when brand changes
      }}
      placeholder="Filter by Brand"
      isClearable
    />
  </div>
</div>

      </div>

      {/* Active Filters */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Filter" size={16} />
            <span>Active filters:</span>
            {productCode && (
              <span className="bg-muted px-2 py-1 rounded text-xs">
                Product Code: "{productCode}"
              </span>
            )}
            {selectedCategory && (
              <span className="bg-muted px-2 py-1 rounded text-xs">
                Category: {reactSelectOptions.find(opt => opt.value === selectedCategory)?.label}
              </span>
            )}
            {selectedBrand && (
              <span className="bg-muted px-2 py-1 rounded text-xs">
                Brand: {brands.find(b => b.brand_id === selectedBrand)?.brand_name}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            iconPosition="left"
            onClick={() => {
              setProductCode('');
              setSelectedCategory(null);
              setSelectedBrand(null);
              if (onClearFilters) onClearFilters();
            }}
          >
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default ProductFilters;
